import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  FormGroup,
  Label,
} from "reactstrap";
import { get, post } from "../../../helpers/api_helper";
import { PageSubHeader } from "../../../components/CommonLayouts/PageSubHeader";
import CustomPagination from "../../../components/CustomPagination";
import SummaryCards from "../../../components/SummaryCards";
import { CustomButton } from "../../../components/CommonInputs/CustomButtons/CustomButton";
import { showErrorToast } from "../../../helpers/toastMessages";
import { formatToPascal } from "../../../helpers/utils/nameUtils";
import * as api from "../../../constants/api_constants";
import secureLocalStorage from "react-secure-storage";

function Payment() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [purposeFilter, setPurposeFilter] = useState("");
  const [transactionModeFilter, setTransactionModeFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sort, setSort] = useState("desc"); // Sort direction: 'asc' or 'desc'
  const [orderBy, setOrderBy] = useState("paidAmount"); // Current sort column
  const timerRef = useRef(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [count, setCount] = useState({
    totalPayable: 0,
    paid: 0,
    partiallyPaid: 0,
    draft: 0,
    overDue: 0,
    dueAmount: 0,
  });
  const [purposes, setPurposes] = useState([
    { value: "", label: "All Purposes" },
    { value: "Salary", label: "Salary" },
    { value: "Vendor Payment", label: "Vendor Payment" },
    { value: "Reimbursement", label: "Reimbursement" },
    { value: "Bonus", label: "Bonus" },
  ]);
  const [transactionModes, setTransactionModes] = useState([
    { value: "", label: "All Transaction Modes" },
    { value: "Bank Transfer", label: "Bank Transfer" },
    { value: "Cash", label: "Cash" },
    { value: "Check", label: "Check" },
    { value: "Online Payment", label: "Online Payment" },
  ]);

  const token = secureLocalStorage.getItem("authToken");

  // Uncomment if APIs exist to fetch unique purposes and transaction modes
  /*
  const fetchPurposes = useCallback(async () => {
    try {
      const response = await get(api.GET_ALL_PURPOSES);
      const purposeList = response?.data?.map((item) => ({
        value: item,
        label: formatToPascal(item),
      })) || [];
      setPurposes([{ value: "", label: "All Purposes" }, ...purposeList]);
    } catch (error) {
      console.error("Error fetching purposes:", error);
      showErrorToast("Failed to fetch purposes.");
    }
  }, []);

  const fetchTransactionModes = useCallback(async () => {
    try {
      const response = await get(api.GET_ALL_TRANSACTION_MODES);
      const modeList = response?.data?.map((item) => ({
        value: item,
        label: formatToPascal(item),
      })) || [];
      setTransactionModes([{ value: "", label: "All Transaction Modes" }, ...modeList]);
    } catch (error) {
      console.error("Error fetching transaction modes:", error);
      showErrorToast("Failed to fetch transaction modes.");
    }
  }, []);
  */

  const fetchPaymentsData = useCallback(
    async (query = "", page = 1, sortDir = sort, sortCol = orderBy) => {
      setIsLoading(true);
      try {
        const response = await post(api.GET_ALL_PAYMENTS, {
          searchKeyword: query?.trim() ?? "",
          page: (page - 1).toString(),
          pageSize: "10",
          sort: sortDir,
          orderBy: sortCol,
        });

        if (response.success) {
          setPayments(response.paginationResponse?.data || []);
          setTotalPages(response.paginationResponse?.totalPages || 0);
        } else {
          setError(`Failed to fetch payments: ${response.message || "Unknown error"}`);
          showErrorToast(`Failed to fetch payments: ${response.message || "Unknown error"}`);
        }
      } catch (error) {
        console.error("Error fetching payments:", {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          url: api.GET_ALL_PAYMENTS,
        });
        let errorMessage = error.message || "Please try again";
        if (error.message.includes("Network Error") || error.message.includes("CORS")) {
          errorMessage = "CORS error: Please ensure the server allows requests from this origin or check your network connection.";
        } else if (error.response?.status === 403) {
          errorMessage = "Access denied: Please verify your authentication token or contact support.";
        }
        setError(`Failed to fetch payments: ${errorMessage}`);
        showErrorToast(`Failed to fetch payments: ${errorMessage}`);
      } finally {
        setIsLoading(false);
      }
    },
    [token]
  );

  const fetchCount = useCallback(async () => {
    setIsLoading(true);
    try {
      console.log("Fetching counts from:", api.GET_PAYMENTS_COUNT);
      console.log("Using token:", token);

      const response = await get(api.GET_PAYMENTS_COUNT);

      if (response.success) {
        setCount({
          totalPayable: response.data?.totalAmount || 0,
          paid: response.data?.paidAmount || 0,
          partiallyPaid: response.data?.partiallyPaidAmount || 0,
          draft: response.data?.draftPaidAmount || 0,
          overDue: response.data?.overdueDueAmount || 0,
          dueAmount: response.data?.totalDueAmount || 0,
        });
      } else {
        setError(`Failed to fetch payment counts: ${response.message || "Unknown error"}`);
        showErrorToast(`Failed to fetch payment counts: ${response.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error fetching payment counts:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: api.GET_PAYMENTS_COUNT,
      });
      let errorMessage = error.message || "Please try again";
      if (error.message.includes("Network Error") || error.message.includes("CORS")) {
        errorMessage = "CORS error: Please ensure the server allows requests from this origin or check your network connection.";
      } else if (error.response?.status === 403) {
        errorMessage = "Access denied: Please verify your authentication token or contact support.";
      }
      setError(`Failed to fetch payment counts: ${errorMessage}`);
      showErrorToast(`Failed to fetch payment counts: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPaymentsData(searchQuery, currentPage, sort, orderBy);
    fetchCount();
  }, [fetchPaymentsData, fetchCount, sort, orderBy, currentPage, searchQuery]);

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      fetchPaymentsData(searchQuery, page, sort, orderBy);
    },
    [fetchPaymentsData, searchQuery, sort, orderBy]
  );

  const handleSearch = useCallback(
    (e) => {
      const query = e.target.value ?? "";
      setSearchQuery(query);
      setCurrentPage(1);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        fetchPaymentsData(query?.trim(), purposeFilter, transactionModeFilter, 1);
      }, 300);
    },
    [fetchPaymentsData, purposeFilter, transactionModeFilter]
  );

  const handlePurposeFilter = useCallback(
    (e) => {
      const value = e.target.value || "";
      setPurposeFilter(value);
      setCurrentPage(1);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        fetchPaymentsData(searchQuery, value, transactionModeFilter, 1);
      }, 300);
    },
    [fetchPaymentsData, searchQuery, transactionModeFilter]
  );

  const handleTransactionModeFilter = useCallback(
    (e) => {
      const value = e.target.value || "";
      setTransactionModeFilter(value);
      setCurrentPage(1);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setCurrentPage(1); // Reset to first page on search
        fetchPaymentsData(query?.trim(), 1, sort, orderBy);
      }, 300);
    },
    [fetchPaymentsData, sort, orderBy]
  );

  const handleSort = (column) => {
    const newSort = orderBy === column && sort === "asc" ? "desc" : "asc";
    setSort(newSort);
    setOrderBy(column);
    setCurrentPage(1); // Reset to first page on sort change
    fetchPaymentsData(searchQuery, 1, newSort, column);
  };

  const getStatusLabelAndColor = (status) => {
    switch (status) {
      case "OVERDUE":
        return { label: "Overdue", color: "text-warning" };
      case "DRAFT":
        return { label: "Draft", color: "text-info" };
      case "PAID":
        return { label: "Paid", color: "text-success" };
      case "PARTIALLY_PAID":
        return { label: "Partially Paid", color: "text-muted" };
      case "PENDING":
        return { label: "Pending", color: "text-secondary" };
      default:
        return { label: status || "Unknown", color: "text-black" };
    }
  };

  const handleEdit = useCallback(
    (id) => {
      navigate(`/add-payment/${id}`);
    },
    [navigate]
  );

  const formatDateManually = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "N/A";
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const summaryData = [
    {
      iconClass: "fas fa-users",
      label: "Total Payables",
      value: count.totalPayable || 0,
    },
    {
      iconClass: "fas fa-user-check text-success",
      label: "Paid",
      value: count.paid || 0,
    },
    {
      iconClass: "fas fa-user-check text-warning",
      label: "Partially Paid",
      value: count.partiallyPaid || 0,
    },
    {
      iconClass: "fas fa-user-check text-info",
      label: "Draft",
      value: count.draft || 0,
    },
    {
      iconClass: "fa-solid fa-circle-half-stroke text-danger",
      label: "Overdue",
      value: count.overDue || 0,
    },
    {
      iconClass: "fas fa-user-check text-danger",
      label: "Due Amount",
      value: count.dueAmount || 0,
    },
  ];

  // const handleEdit = useCallback(
  //   (id) => {
  //     navigate(`/add-payment/${id}`);
  //   },
  //   [navigate]
  // );

  // const formatDateManually = (date) => {
  //   if (!date) return "";
  //   const d = new Date(date);
  //   if (isNaN(d.getTime())) return "";
  //   const month = (d.getMonth() + 1).toString().padStart(2, "0");
  //   const day = d.getDate().toString().padStart(2, "0");
  //   const year = d.getFullYear();
  //   return `${month}/${day}/${year}`;
  // };

  const getSortIcon = (column) => {
    if (orderBy === column) {
      return sort === "asc" ? (
        <i className="fas fa-sort-up ms-1" />
      ) : (
        <i className="fas fa-sort-down ms-1" />
      );
    }
    return <i className="fas fa-sort ms-1" />;
  };

  return (
    <div className="page-content">
      <Container fluid={true} className="py-0 px-0">
        <PageSubHeader
          heading={"Payouts"}
          routePath={"Home"}
          pageName={"Payouts"}
        />
        <Row className="px-4">
          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          <SummaryCards data={summaryData} />
          <Row>
            <Col>
              <Row className="d-flex justify-content-between align-items-center py-2">
                <Col xs={12} sm={4}>
                  <InputGroup size="sm" className="elbert-search-group">
                    <Input
                      placeholder="Search, Paid To, Department, Purpose"
                      className="elbert-search"
                      onChange={handleSearch}
                      value={searchQuery}
                      disabled={isLoading}
                      aria-label="Search payments"
                    />
                    <InputGroupText>
                      {isLoading ? (
                        <i className="fas fa-spinner fa-spin" />
                      ) : (
                        <i className="fa fa-search" />
                      )}
                    </InputGroupText>
                  </InputGroup>
                </Col>
                <Col xs={12} sm={3}>
                  <FormGroup>
                    <Label className="fw-bold">Purpose</Label>
                    <Input
                      type="select"
                      value={purposeFilter}
                      onChange={handlePurposeFilter}
                      disabled={isLoading}
                      aria-label="Filter by purpose"
                    >
                      {purposes.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col xs={12} sm={3}>
                  <FormGroup>
                    <Label className="fw-bold">Transaction Mode</Label>
                    <Input
                      type="select"
                      value={transactionModeFilter}
                      onChange={handleTransactionModeFilter}
                      disabled={isLoading}
                      aria-label="Filter by transaction mode"
                    >
                      {transactionModes.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col
                  xs={12}
                  sm={2}
                  className="d-flex align-items-center justify-content-sm-end justify-content-center mt-2 mt-sm-0"
                >
                  <CustomButton
                    text="+ Add New"
                    onClick={() => navigate("/add-payment")}
                    disabled={isLoading}
                    aria-label="Add new payment"
                  />
                </Col>
              </Row>
              <Row>
                <Card>
                  <CardBody>
                    <div className="table-responsive">
                      <Table className="table mb-0">
                        <thead>
                          <tr>
                            <th
                              onClick={() => handleSort("paidTo")}
                              style={{ cursor: "pointer" }}
                            >
                              Paid To {getSortIcon("paidTo")}
                            </th>
                            <th
                              onClick={() => handleSort("primaryContact")}
                              style={{ cursor: "pointer" }}
                            >
                              Primary Contact {getSortIcon("primaryContact")}
                            </th>
                            <th
                              onClick={() => handleSort("amountToBePaid")}
                              style={{ cursor: "pointer" }}
                            >
                              Amount To Be Paid {getSortIcon("amountToBePaid")}
                            </th>
                            <th
                              onClick={() => handleSort("paidAmount")}
                              style={{ cursor: "pointer" }}
                            >
                              Paid Amount {getSortIcon("paidAmount")}
                            </th>
                            <th
                              onClick={() => handleSort("dueAmount")}
                              style={{ cursor: "pointer" }}
                            >
                              Due Amount {getSortIcon("dueAmount")}
                            </th>
                            <th
                              onClick={() => handleSort("dueDate")}
                              style={{ cursor: "pointer" }}
                            >
                              Due Date {getSortIcon("dueDate")}
                            </th>
                            <th
                              onClick={() => handleSort("purpose")}
                              style={{ cursor: "pointer" }}
                            >
                              Purpose {getSortIcon("purpose")}
                            </th>
                            <th
                              onClick={() => handleSort("transactionMode")}
                              style={{ cursor: "pointer" }}
                            >
                              Transaction Mode {getSortIcon("transactionMode")}
                            </th>
                            <th
                              onClick={() => handleSort("refundable")}
                              style={{ cursor: "pointer" }}
                            >
                              Refundable {getSortIcon("refundable")}
                            </th>
                            <th
                              onClick={() => handleSort("status")}
                              style={{ cursor: "pointer" }}
                            >
                              Status {getSortIcon("status")}
                            </th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <tr>
                              <td colSpan="11" className="text-center">
                                <i className="fas fa-spinner fa-spin" /> Loading payments...
                              </td>
                            </tr>
                          ) : payments && payments.length > 0 ? (
                            payments.map((payment, index) => {
                              const { label, color } = getStatusLabelAndColor(payment.status);
                              return (
                                <tr key={index}>
                                  <td>
                                    {formatToPascal(payment.paidTo || "")}
                                  </td>
                                  <td>
                                    {formatToPascal(
                                      payment.primaryContact || ""
                                    )}
                                  </td>
                                  <td style={{ textAlign: "right" }}>
                                    {payment.amountToBePaid || 0}
                                  </td>
                                  <td style={{ textAlign: "right" }}>
                                    {payment.paidAmount || 0}
                                  </td>
                                  <td style={{ textAlign: "right" }}>
                                    {payment.dueAmount || 0}
                                  </td>
                                  <td>{formatDateManually(payment.dueDate)}</td>
                                  <td>{formatToPascal(payment.purpose || "N/A")}</td>
                                  <td>{formatToPascal(payment.transactionMode || "N/A")}</td>
                                  <td>{formatToPascal(payment.refundable || "No")}</td>
                                  <td className={color}>{label}</td>
                                  <td>
                                    <div className="d-flex gap-3 align-items-center justify-content-center">
                                      <i
                                        className="fa-solid fa-eye action-icon"
                                        title="View"
                                        onClick={() => navigate(`/view-payment/${payment.id}`)}
                                        aria-label={`View payment ${payment.id || "unknown"}`}
                                      ></i>
                                      <i
                                        onClick={() => handleEdit(payment.id)}
                                        className="fa-regular fa-pen-to-square action-icon"
                                        title="Edit"
                                        aria-label={`Edit payment ${payment.id || "unknown"}`}
                                      ></i>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="11" className="text-center">
                                {searchQuery || purposeFilter || transactionModeFilter
                                  ? `No payments found for the selected filters.`
                                  : "No payments found."}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </Row>
            </Col>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            disabled={isLoading}
            aria-label="Payments pagination"
          />
        </Row>
      </Container>
    </div>
  );
}

export default Payment;